// ABOUTME: The eight 2A feeling scenes (Angry, Sad, Scared, Worried, Happy, Excited, Yucky, Surprised), one looping scene each.
// ABOUTME: Positions are the Feeling Selection prototype's own, as percentages of its 850 x 370 stage; reduced motion holds each part at its peak frame.

import type { ReactElement, ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';

// The keyframes, verbatim from the prototype. Defined once on the stage that holds the scene.
export const FEELING_KEYFRAMES = {
  '@keyframes flFlare': { '0%,100%': { transform: 'scale(0.86)', opacity: 0.75 }, '42%': { transform: 'scale(1.18)', opacity: 1 }, '62%': { transform: 'scale(1.1)', opacity: 0.95 } },
  '@keyframes flHeatRise': { '0%': { transform: 'translateY(0)', opacity: 0 }, '18%': { opacity: 0.8 }, '78%': { transform: 'translateY(-40px)', opacity: 0.5 }, '100%': { transform: 'translateY(-52px)', opacity: 0 } },
  '@keyframes flSadSink': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(9px)' } },
  '@keyframes flDrip': { '0%,8%': { transform: 'translateY(-4px)', opacity: 0 }, '22%': { opacity: 0.85 }, '72%': { transform: 'translateY(32px)', opacity: 0.6 }, '92%,100%': { transform: 'translateY(42px)', opacity: 0 } },
  '@keyframes flLoom': { '0%,12%': { transform: 'scaleY(0.2)', opacity: 0 }, '40%': { transform: 'scaleY(1)', opacity: 0.85 }, '58%': { transform: 'scaleY(0.96)', opacity: 0.85 }, '88%,100%': { transform: 'scaleY(0.2)', opacity: 0 } },
  '@keyframes flDim': { '0%,14%': { opacity: 0 }, '44%': { opacity: 0.5 }, '60%': { opacity: 0.5 }, '90%,100%': { opacity: 0 } },
  '@keyframes flOrbit': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
  '@keyframes flLoopPulse': { '0%,100%': { transform: 'scale(0.94)', opacity: 0.5 }, '50%': { transform: 'scale(1.05)', opacity: 0.85 } },
  '@keyframes flSunOpen': { '0%,10%': { transform: 'scale(0.55)', opacity: 0.25 }, '46%': { transform: 'scale(1)', opacity: 1 }, '78%': { transform: 'scale(1.05)', opacity: 1 }, '96%,100%': { transform: 'scale(0.55)', opacity: 0.25 } },
  '@keyframes flRaySpin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(180deg)' } },
  '@keyframes flSparkPop': { '0%,10%': { transform: 'scale(0.2)', opacity: 0 }, '20%': { transform: 'scale(1.15)', opacity: 1 }, '32%': { transform: 'scale(1)', opacity: 0.9 }, '46%,100%': { transform: 'scale(0.2)', opacity: 0 } },
  '@keyframes flRingOut': { '0%,8%': { transform: 'scale(0.25)', opacity: 0 }, '16%': { opacity: 0.8 }, '56%': { transform: 'scale(1)', opacity: 0.25 }, '68%,100%': { transform: 'scale(1.08)', opacity: 0 } },
  '@keyframes flDriftUp': { '0%': { transform: 'translateY(10px) translateX(-5px)', opacity: 0 }, '22%': { opacity: 0.8 }, '74%': { transform: 'translateY(-28px) translateX(5px)', opacity: 0.5 }, '100%': { transform: 'translateY(-38px) translateX(7px)', opacity: 0 } },
  '@keyframes flGreenPuff': { '0%,100%': { transform: 'scale(0.94) translateY(1px)' }, '50%': { transform: 'scale(1.05) translateY(-3px)' } },
  '@keyframes flBurst': { '0%,8%': { transform: 'scale(0.12)', opacity: 0 }, '14%': { opacity: 0.9 }, '52%': { transform: 'scale(1)', opacity: 0.35 }, '64%,100%': { transform: 'scale(1.12)', opacity: 0 } },
  '@keyframes flJump': { '0%,8%': { transform: 'translateY(0)' }, '16%': { transform: 'translateY(-16px)' }, '24%': { transform: 'translateY(-12px)' }, '34%': { transform: 'translateY(-14px)' }, '52%,100%': { transform: 'translateY(0)' } },
} as const;

type Keyframe = 'flFlare' | 'flHeatRise' | 'flSadSink' | 'flDrip' | 'flLoom' | 'flDim' | 'flOrbit' | 'flLoopPulse' | 'flSunOpen'
  | 'flRaySpin' | 'flSparkPop' | 'flRingOut' | 'flDriftUp' | 'flGreenPuff' | 'flBurst' | 'flJump';

// Each part's frame under reduced motion: its keyframe at the stop where it is most fully drawn.
const PEAK: Record<Keyframe, Record<string, string | number>> = {
  flFlare: { transform: 'scale(1.18)', opacity: 1 },
  flHeatRise: { transform: 'translateY(-9px)', opacity: 0.8 },
  flSadSink: { transform: 'translateY(9px)' },
  flDrip: { transform: 'translateY(4px)', opacity: 0.85 },
  flLoom: { transform: 'scaleY(1)', opacity: 0.85 },
  flDim: { opacity: 0.5 },
  flOrbit: { transform: 'rotate(0deg)' },
  flLoopPulse: { transform: 'scale(1.05)', opacity: 0.85 },
  flSunOpen: { transform: 'scale(1)', opacity: 1 },
  flRaySpin: { transform: 'rotate(0deg)' },
  flSparkPop: { transform: 'scale(1.15)', opacity: 1 },
  flRingOut: { transform: 'scale(1)', opacity: 0.25 },
  flDriftUp: { transform: 'translateY(-1px) translateX(-2px)', opacity: 0.8 },
  flGreenPuff: { transform: 'scale(1.05) translateY(-3px)' },
  flBurst: { transform: 'scale(1)', opacity: 0.35 },
  flJump: { transform: 'translateY(-16px)' },
};

interface PartProps {
  kf?: Keyframe;
  duration?: string;
  ease?: string;
  delay?: string;
  // Replaces the peak frame for a part that should sit apart from its twin under reduced motion.
  peak?: Record<string, string | number>;
  testId?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
}

// One decorative part of a scene: absolutely positioned on the stage, looping its keyframe or, under reduced motion, held at its peak.
function Part({ kf, duration = '5s', ease = 'ease-in-out', delay, peak, testId, sx, children }: PartProps) {
  const reducedMotion = useCrossingReducedMotion();
  const motion = !kf ? {} : reducedMotion
    ? (peak ?? PEAK[kf])
    : { animation: `${kf} ${duration} ${ease} infinite`, ...(delay ? { animationDelay: delay } : {}) };
  return (
    <Box aria-hidden="true" data-testid={testId} sx={{ position: 'absolute', ...(sx as object), ...motion }}>
      {children}
    </Box>
  );
}

const pill = (background: string, height: number): SxProps<Theme> => ({ position: 'absolute', left: 0, bottom: 0, width: '100%', height, borderRadius: '999px', background });
const puff = (background: string, bottom: number, height: number): SxProps<Theme> => ({ position: 'absolute', left: '22%', bottom, width: '52%', height, borderRadius: '50%', background });

function Angry() {
  const heat = (left: string, top: string, height: number, alpha: number, delay?: string) => (
    <Part key={left} testId="heat-line" kf="flHeatRise" duration="5s" ease="linear" delay={delay}
      sx={{ left, top, width: 3, height, borderRadius: '2px', background: `rgba(224,140,116,${alpha})` }} />
  );
  return (
    <>
      <Part kf="flFlare" sx={{ left: '50%', top: '16%', width: 150, height: 70, marginLeft: '-75px', transformOrigin: '50% 100%' }}>
        <Box sx={pill('#E08C74', 40)} />
        <Box sx={puff('#E89B84', 22, 42)} />
        <Box sx={{ position: 'absolute', left: '50%', bottom: 32, width: '32%', height: 34, borderRadius: '50%', background: '#F0B3A0' }} />
      </Part>
      {heat('40%', '44%', 22, 0.8)}
      {heat('50%', '46%', 26, 0.7, '-1.6s')}
      {heat('60%', '44%', 20, 0.6, '-3.2s')}
    </>
  );
}

function Sad() {
  const drip = (left: string, top: string, alpha: number, delay?: string) => (
    <Part key={left} testId="drip" kf="flDrip" duration="5s" ease="linear" delay={delay}
      sx={{ left, top, width: 3, height: 15, borderRadius: '2px', background: `rgba(143,168,200,${alpha})` }} />
  );
  return (
    <>
      <Part kf="flSadSink" sx={{ left: '50%', top: '19%', width: 160, height: 66, marginLeft: '-80px' }}>
        <Box sx={pill('#8FA8C8', 38)} />
        <Box sx={puff('#9DB3CF', 22, 40)} />
      </Part>
      {drip('43%', '41%', 0.9)}
      {drip('51%', '42%', 0.8, '-1.7s')}
      {drip('58%', '41%', 0.7, '-3.4s')}
    </>
  );
}

// The dim covers the sky above the horizon; the scene host draws it viewport-wide so it never shows an edge.
export function ScaredDim() {
  return (
    <Part testId="sky-dim" kf="flDim" sx={{ inset: 0, background: 'linear-gradient(rgba(59,67,80,0.4), rgba(59,67,80,0.06) 60%)' }} />
  );
}

function Scared() {
  return (
    <>
      <Part testId="loom" kf="flLoom"
        sx={{ left: '24%', top: 'calc(57% - 84px)', width: 172, height: 84, borderRadius: '60% 90% 0 0', background: 'linear-gradient(#7A87A8, #93A0BC)', transformOrigin: '50% 100%' }} />
      <Part testId="loom-highlight" kf="flLoom"
        sx={{ left: '28%', top: 'calc(57% - 92px)', width: 40, height: 14, borderRadius: '999px', background: 'rgba(251,246,234,0.75)', transformOrigin: '50% 50%' }} />
    </>
  );
}

function Worried() {
  const ring = { left: '50%', top: '29%', width: 124, height: 124, marginLeft: '-62px', marginTop: '-62px' };
  return (
    <>
      <Part testId="ring" kf="flLoopPulse" sx={{ ...ring, borderRadius: '50%', border: '2.5px dashed rgba(184,166,196,0.75)', boxSizing: 'border-box' }} />
      <Part testId="orbit" kf="flOrbit" duration="5s" ease="linear" sx={ring}>
        <Box sx={{ position: 'absolute', left: '50%', top: -5, width: 11, height: 11, marginLeft: '-5.5px', borderRadius: '50%', background: '#B8A6C4' }} />
      </Part>
      <Part sx={{ left: '50%', top: '29%', width: 48, height: 22, marginLeft: '-24px', marginTop: '-11px' }}>
        <Box sx={pill('#C6C3D6', 14)} />
        <Box sx={{ position: 'absolute', left: '26%', bottom: 7, width: '48%', height: 14, borderRadius: '50%', background: '#CFCCDD' }} />
      </Part>
    </>
  );
}

function Happy() {
  return (
    <>
      <Part testId="rays" kf="flRaySpin" duration="14s" ease="linear"
        sx={{ left: '50%', top: '26%', width: 104, height: 104, marginLeft: '-52px', marginTop: '-52px' }}>
        {[0, 60, 120].map((deg) => (
          <Box key={deg} data-testid="ray"
            sx={{ position: 'absolute', left: '50%', top: 0, width: '2.5px', height: '100%', marginLeft: '-1.25px', background: 'rgba(246,212,138,0.55)', borderRadius: '2px', transform: deg ? `rotate(${deg}deg)` : undefined }} />
        ))}
      </Part>
      <Part testId="sun" kf="flSunOpen"
        sx={{ left: '50%', top: '26%', width: 88, height: 88, marginLeft: '-44px', marginTop: '-44px', borderRadius: '50%', background: 'radial-gradient(circle, #F6D48A 0 38%, rgba(246,212,138,0.42) 38% 62%, rgba(246,212,138,0) 78%)' }} />
      <Part testId="light-column" kf="flSunOpen"
        sx={{ left: '50%', top: 'calc(57% + 4px)', width: 26, height: 58, marginLeft: '-13px', borderRadius: '12px', background: 'linear-gradient(rgba(246,212,138,0.45), rgba(246,212,138,0.05))' }} />
    </>
  );
}

function Excited() {
  const ring = { left: '50%', top: 'calc(57% + 6px)', width: 104, height: 38, marginLeft: '-52px', borderRadius: '50%', boxSizing: 'border-box' } as const;
  const spark = (left: string, top: string, size: number, color: string, delay?: string) => {
    const mid = Math.floor((size - 3) / 2);
    return (
      <Part key={left} testId="spark" kf="flSparkPop" duration="2.5s" delay={delay} sx={{ left, top, width: size, height: size }}>
        <Box sx={{ position: 'absolute', left: mid, top: 0, width: 3, height: size, borderRadius: '2px', background: color }} />
        <Box sx={{ position: 'absolute', left: 0, top: mid, width: size, height: 3, borderRadius: '2px', background: color }} />
      </Part>
    );
  };
  return (
    <>
      <Part testId="water-ring" kf="flRingOut" duration="2.5s" ease="ease-out" sx={{ ...ring, border: '2.5px solid rgba(127,176,164,0.7)' }} />
      <Part testId="water-ring" kf="flRingOut" duration="2.5s" ease="ease-out" delay="0.5s" peak={{ transform: 'scale(0.7)', opacity: 0.5 }}
        sx={{ ...ring, border: '2px solid rgba(127,176,164,0.5)' }} />
      {spark('36%', '33%', 15, '#F6D48A')}
      {spark('61%', '26%', 13, '#F0B37E', '0.6s')}
      {spark('50%', '20%', 11, '#7FB0A4', '1.2s')}
    </>
  );
}

function Yucky() {
  const strokes = (top: number, left: number, width: string, rotate: number, alpha: number, rgb: string) => (
    <Box sx={{ position: 'absolute', left, top, width, height: 3, borderRadius: '2px', background: `rgba(${rgb},${alpha})`, transform: `rotate(${rotate}deg)` }} />
  );
  return (
    <>
      <Part kf="flGreenPuff" sx={{ left: '50%', top: '18%', width: 142, height: 60, marginLeft: '-71px' }}>
        <Box sx={pill('#A9B98A', 36)} />
        <Box sx={puff('#B7C49A', 20, 38)} />
      </Part>
      <Part testId="drift" kf="flDriftUp" duration="5s" ease="linear" sx={{ left: '42%', top: '38%', width: 28, height: 20 }}>
        {strokes(0, 0, '100%', -10, 0.85, '157,169,120')}
        {strokes(9, 2, '82%', 9, 0.65, '157,169,120')}
      </Part>
      <Part testId="drift" kf="flDriftUp" duration="5s" ease="linear" delay="-2.5s" sx={{ left: '56%', top: '42%', width: 24, height: 18 }}>
        {strokes(0, 0, '100%', 9, 0.8, '169,155,107')}
        {strokes(8, 2, '78%', -8, 0.6, '169,155,107')}
      </Part>
    </>
  );
}

function Surprised() {
  const ring = { left: '50%', top: '29%', width: 132, height: 132, marginLeft: '-66px', marginTop: '-66px', borderRadius: '50%', boxSizing: 'border-box' } as const;
  return (
    <>
      <Part testId="burst" kf="flBurst" ease="ease-out" sx={{ ...ring, border: '2.5px solid rgba(147,169,190,0.8)' }} />
      <Part testId="burst" kf="flBurst" ease="ease-out" delay="0.35s" peak={{ transform: 'scale(0.8)', opacity: 0.5 }} sx={{ ...ring, border: '2px solid rgba(147,169,190,0.6)' }} />
      <Part testId="jump" kf="flJump" sx={{ left: '50%', top: '29%', width: 60, height: 28, marginLeft: '-30px', marginTop: '-14px' }}>
        <Box sx={pill('#93A9BE', 17)} />
        <Box sx={puff('#A3B7C9', 10, 17)} />
      </Part>
    </>
  );
}

export const FEELING_SCENES: Record<string, () => ReactElement> = {
  angry: Angry, sad: Sad, scared: Scared, worried: Worried, happy: Happy, excited: Excited, yucky: Yucky, surprised: Surprised,
};

// The main figure of each scene: its width and centre in stage px, taken from the parts above. FeelingScene scales the whole
// art about this centre so the figure reads at FEELING_SPRITE_PX.
export const FEELING_SPRITE_PX = 150;
export const FEELING_FIGURE: Record<string, { width: number; cx: number; cy: number }> = {
  angry: { width: 150, cx: 425, cy: 94.2 },
  sad: { width: 160, cx: 425, cy: 103.3 },
  scared: { width: 172, cx: 290, cy: 168.9 }, // the loom
  worried: { width: 124, cx: 425, cy: 107.3 }, // the ring
  happy: { width: 88, cx: 425, cy: 96.2 }, // the sun
  excited: { width: 104, cx: 425, cy: 235.9 }, // the water ring
  yucky: { width: 142, cx: 425, cy: 96.6 },
  surprised: { width: 132, cx: 425, cy: 107.3 }, // the burst
};
